import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { Spinner } from '@/components/admin/ui';
import {
  EmptyState,
} from '@/components';
import type { DictionaryBrowseEntry } from '@/lib/admin-types';
import { apiJson } from '@/lib/api';
import { DictionaryEntryEditor } from './-dictionary-shared';

export const Route = createFileRoute('/admin/dictionary/entry/$id')({
  component: DictionaryEntryPage,
});

function DictionaryEntryPage() {
  const { id: idParam } = Route.useParams();
  const navigate = useNavigate();
  const entryId = Number(idParam);

  const entryQ = useQuery({
    queryKey: ['admin', 'dictionary', 'entry', entryId],
    queryFn: () =>
      apiJson<DictionaryBrowseEntry>(`/v1/admin/dictionary?id=${entryId}`),
    enabled: Number.isFinite(entryId) && entryId >= 1,
  });

  if (!Number.isFinite(entryId) || entryId < 1) {
    return <EmptyState title="Invalid entry id" />;
  }

  if (entryQ.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (entryQ.isError || !entryQ.data) {
    return (
      <EmptyState
        title="Could not load entry"
        hint={(entryQ.error as Error)?.message}
      />
    );
  }

  const entry = entryQ.data;

  return (
    <div className="space-y-4">
      <Link
        to="/admin/dictionary/list"
        search={{ status: 'all', page: 1 }}
        className="inline-flex text-sm font-medium text-nova-muted-foreground hover:text-nova-foreground"
      >
        ← Back to browse
      </Link>

      <DictionaryEntryEditor
        entry={entry}
        entryQueryKey={['admin', 'dictionary', 'entry', entryId]}
        positionLabel={`Entry ${entry.index + 1} of ${entry.total}`}
        onPrev={() => {
          if (entry.id > 1) {
            void navigate({
              to: '/admin/dictionary/entry/$id',
              params: { id: String(entry.id - 1) },
            });
          }
        }}
        onNext={() => {
          if (entry.id < entry.total) {
            void navigate({
              to: '/admin/dictionary/entry/$id',
              params: { id: String(entry.id + 1) },
            });
          }
        }}
        disablePrev={entry.id <= 1}
        disableNext={entry.id >= entry.total}
        showReviewActions
        onReviewStatusApplied={() => {
          void entryQ.refetch();
        }}
      />
    </div>
  );
}
